import {useDispatch} from "react-redux";
import {useState} from "react";
import {Form, Input, Modal, Checkbox, Divider, Select, DatePicker, Button, Space} from "antd";
import {updateLetterMetadata} from "../../store/app/app.slice";
import {message,notification} from "antd";
import {getLanguagesOptions, getTopicsOptions, getTopicsOptionsUrdu, getCountriesOptions} from "../helper/labelHelper";
import {countries} from "../../utils/labelHelper";
import moment from "moment";


export default function EditSenderDialog({showModal, onHideModal, letter, onSenderUpdateDone}) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [sender, setSender] = useState(letter?.originalLetter?.sender || {});
    const [isGlobal, setIsGlobal] = useState(true);
    const isUrduLanguage = letter?.language === 'urdu';

    const hideModal = () => {
        form.resetFields();
        setIsGlobal(true);
        onHideModal();
    }

    const handleSubmit = () => {
        form.validateFields().then(() => {
            dispatch(updateLetterMetadata({
                finalData: {
                    letterId: letter.id,
                    metadata: {
                        sender: {...sender, birthdate: form.getFieldValue('birthdate')},
                        isGlobal: isGlobal,
                        language: form.getFieldValue('language'),
                        topic: form.getFieldValue('topic')
                    }
                },
                callbackSuccess: (result) => {
                    console.log(result);
                    message.success("Sender information updated successfully");
                    onSenderUpdateDone().then(() => {
                        hideModal();
                    });
                },
                callbackError: (error) => {
                    console.log(error);
                    notification['error']({
                        message: 'Error',
                        description: JSON.stringify(error),
                        duration: 0,
                        placement: 'top',
                    });
                },
            }));
        }).catch((errorInfo) => {
            const missingFields = (errorInfo?.errorFields || [])
                .map(f => f.errors?.[0])
                .filter(Boolean);
            notification['error']({
                message: 'Please complete the required fields',
                description: missingFields.length
                    ? missingFields.join(', ')
                    : 'Some required fields are missing or invalid.',
                placement: 'top',
            });
        });
    }

    const handleGlobalChange = (e) => {
        setIsGlobal(e.target.checked);
    };

    return <>
        <Modal
            title="Update Letter Metadata"
            open={showModal}
            onCancel={hideModal}
            footer={null}
            style={{ top: 24 }}
            bodyStyle={{
                paddingTop: 0,
                maxHeight: 'calc(100vh - 160px)',
                overflowY: 'auto'
            }}
        >
            {/* Sticky action bar: stays visible while the form below scrolls */}
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: '#fff',
                    paddingTop: 16,
                    paddingBottom: 12,
                    marginBottom: 4,
                    borderBottom: '1px solid #f0f0f0'
                }}
            >
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={hideModal}>Cancel</Button>
                    <Button type="primary" onClick={handleSubmit}>Save</Button>
                </Space>
                <div style={{ marginTop: 8 }}>
                    <Checkbox
                        checked={isGlobal}
                        onChange={handleGlobalChange}
                    >
                        Update sender information globally
                    </Checkbox>
                    <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                        This action will update the user's account information globally and will apply to all future correspondence, not just this letter.
                    </div>
                </div>
            </div>
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                initialValues={{
                    ...sender,
                    language: letter?.originalLetter?.language,
                    topic: letter?.originalLetter?.category,
                    birthdate: sender.birthdate ? moment.utc(sender.birthdate) : null,
                    country: sender.country?.key
                }}
                style={{ marginTop: 0 }}
            >
                <Divider style={{ marginTop: 0 }}>Sender Information</Divider>
                <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{required: true, message: 'Please enter the first name'}]}
                >
                    <Input 
                        value={sender.firstName}
                        onChange={(e) => setSender({...sender, firstName: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{required: true, message: 'Please enter the last name'}]}
                >
                    <Input 
                        value={sender.lastName}
                        onChange={(e) => setSender({...sender, lastName: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="urduName"
                    label="Urdu Name"
                    rules={[{required: true, message: 'Please enter the Urdu name'}]}
                >
                    <Input 
                        className="arabic-font arabic-font-edit"
                        value={sender.urduName}
                        onChange={(e) => setSender({...sender, urduName: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="gender"
                    label="Gender"
                    rules={[{required: true, message: 'Please select the gender'}]}
                >
                    <Select
                        value={sender.gender}
                        onChange={(value) => setSender({...sender, gender: value})}
                        options={[
                            {value: 'MALE', label: 'Male'},
                            {value: 'FEMALE', label: 'Female'}
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    name="streetLine1"
                    label="Street Line 1"
                    rules={[{required: true, message: 'Please enter the street address'}]}
                >
                    <Input 
                        value={sender.streetLine1}
                        onChange={(e) => setSender({...sender, streetLine1: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="streetLine2"
                    label="Street Line 2"
                >
                    <Input 
                        value={sender.streetLine2}
                        onChange={(e) => setSender({...sender, streetLine2: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="zipCode"
                    label="ZIP Code"
                    rules={[{required: true, message: 'Please enter the ZIP code'}]}
                >
                    <Input 
                        value={sender.zipCode}
                        onChange={(e) => setSender({...sender, zipCode: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="city"
                    label="City"
                    rules={[{required: true, message: 'Please enter the city'}]}
                >
                    <Input 
                        value={sender.city}
                        onChange={(e) => setSender({...sender, city: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="state"
                    label="State"
                >
                    <Input
                        value={sender.state}
                        onChange={(e) => setSender({...sender, state: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="country"
                    label="Country"
                    rules={[{required: true, message: 'Please select the country'}]}
                >
                    <Select
                        value={sender.country?.key}
                        onChange={(value) => {
                            const selectedCountry = countries.find(c => c.key === value);
                            setSender({
                                ...sender,
                                country: {
                                    key: selectedCountry.key,
                                    defaultLabel: selectedCountry.defaultLabel,
                                    values: { flag: selectedCountry.flag }
                                }
                            });
                        }}
                    >
                        {getCountriesOptions()}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="aimsId"
                    label="AIMS ID"
                >
                    <Input 
                        value={sender.aimsId}
                        onChange={(e) => setSender({...sender, aimsId: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="jamaat"
                    label="Jamaat"
                >
                    <Input 
                        value={sender.jamaat}
                        onChange={(e) => setSender({...sender, jamaat: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="contactNumber"
                    label="Contact Number"
                    rules={[{required: true, message: 'Please enter the contact number'}]}
                >
                    <Input 
                        value={sender.contactNumber}
                        onChange={(e) => setSender({...sender, contactNumber: e.target.value})}
                    />
                </Form.Item>
                <Form.Item
                    name="birthdate"
                    label="Date of Birth"
                    rules={[{required: true, message: 'Please select the date of birth'}]}
                >
                    <DatePicker />
                </Form.Item>
                <Divider>Letter Information</Divider>
                <Form.Item
                    name="language"
                    label="Language"
                    rules={[{required: true, message: 'Please select the language'}]}
                >
                    <Select>{getLanguagesOptions()}</Select>
                </Form.Item>
                <Form.Item
                    name="topic"
                    label="Topic"
                    rules={[{required: true, message: 'Please select the topic'}]}
                >
                    <Select
                        className={isUrduLanguage ? "arabic-font arabic-font-extra-small" : ""}
                        popupClassName={isUrduLanguage ? "arabic-font arabic-font-edit" : ""}
                    >{isUrduLanguage ? getTopicsOptionsUrdu() : getTopicsOptions()}</Select>
                </Form.Item>
            </Form>
        </Modal>
    </>;
} 